import React, { useEffect, useRef, useState } from 'react';
import Campaigns from './components/Campaigns';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import UserManagement from './components/UserManagement';
import Dashboard from './components/Dashboard';
import Analysis from './components/Analysis';
import Activists from './components/Activists';
import Reports from './components/Reports';
import Performance from './components/Performance';
import LandingPage from './components/LandingPage';
import SovraLandingPage from './components/SovraLandingPage';
import SecTraceLandingPage from './components/SecTraceLandingPage';
import SecTraceResearch from './components/SecTraceResearch';
import ActivistProfile from './components/ActivistProfile';
import AddReport from './components/AddReport';
import TwitterFeed from './components/TwitterFeed';
import Navigation from './components/Navigation';
import ReportDetails from './components/ReportDetails';
import Login from './components/Login';
import Subscriptions from './components/Subscriptions';
import ScraperMonitoring from './components/ScraperMonitoring';
import BacktestLive from './components/BacktestLive';

// Component to handle scroll to top on route changes
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Cinematic two-panel route transition. Intercepts same-origin anchor
// clicks so the outgoing page is visually "closed" by a pair of cream
// panels meeting at the center, the new route mounts silently behind
// them, and the panels then part to reveal it.
//
// — Respects prefers-reduced-motion (falls through to default navigation).
// — Preserves modifier-click, middle-click, target=_blank, mailto/tel,
//   hash-only anchors, external URLs, and same-pathname clicks.
function RouteTransition() {
  const navigate = useNavigate();
  const location = useLocation();
  const [phase, setPhase] = useState('idle'); // 'idle' | 'prep' | 'cover' | 'reveal'
  const phaseRef = useRef('idle');
  const reducedMotion = useRef(
    typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  const setP = (p) => {
    phaseRef.current = p;
    setPhase(p);
  };

  useEffect(() => {
    if (reducedMotion.current) return;

    const handleClick = (e) => {
      if (phaseRef.current !== 'idle') return;

      const anchor = e.target && e.target.closest && e.target.closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;
      if (/^(https?:|mailto:|tel:)/i.test(href)) return;
      if (href.startsWith('#')) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

      let url;
      try { url = new URL(href, window.location.origin); }
      catch { return; }
      if (url.origin !== window.location.origin) return;
      if (url.pathname === location.pathname) return;

      e.preventDefault();
      e.stopPropagation();

      // prep: mount panels off-screen with no transition so the
      // subsequent "cover" commit animates from the correct start state
      setP('prep');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setP('cover');
          // after panels fully close, swap the route and part them
          setTimeout(() => {
            navigate(url.pathname + url.search + url.hash);
            setP('reveal');
            setTimeout(() => setP('idle'), 560);
          }, 500); // 460ms cover + ~40ms hold
        });
      });
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [navigate, location.pathname]);

  if (phase === 'idle') return null;

  const transition =
    phase === 'prep'
      ? 'none'
      : phase === 'cover'
        ? 'transform 460ms cubic-bezier(0.16, 1, 0.3, 1)'    // expo-out → smooth deceleration into meet
        : 'transform 540ms cubic-bezier(0.7, 0, 0.84, 0)';   // expo-in → crisp acceleration away

  const topTransform = phase === 'cover' ? 'translateY(0%)' : 'translateY(-100%)';
  const botTransform = phase === 'cover' ? 'translateY(0%)' : 'translateY(100%)';

  const baseStyle = {
    position: 'fixed',
    left: 0,
    right: 0,
    height: '51vh', // 1vh overlap so the two panels meet with no seam
    background: '#F5F1EA',
    pointerEvents: phase === 'cover' ? 'auto' : 'none',
    zIndex: 9999,
    transition,
    willChange: 'transform',
    boxShadow: '0 0 0 1px rgba(0,0,0,0.0)' // hint to force a compositor layer
  };

  return (
    <>
      <div aria-hidden style={{ ...baseStyle, top: 0, transform: topTransform }} />
      <div aria-hidden style={{ ...baseStyle, bottom: 0, transform: botTransform }} />
    </>
  );
}

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
    <AuthProvider>
      <Router>
        <ScrollToTop />
        <RouteTransition />
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Navigate to="/phalanx" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/phalanx" element={<LandingPage />} />
          <Route path="/sovra" element={<SovraLandingPage />} />
          <Route path="/sectrace" element={<SecTraceLandingPage />} />
          <Route path="/sectrace/research" element={<SecTraceResearch />} />
          <Route path="/subscriptions" element={<Subscriptions />} />
          
          {/* Protected Routes - Require Login */}
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Navigation />
              <main className="min-h-screen bg-white">
                <Dashboard />
              </main>
            </ProtectedRoute>
          } />
          <Route path="/analysis" element={
            <ProtectedRoute requireFullAccess>
              <Navigation />
              <main className="min-h-screen bg-gray-50">
                <Analysis />
              </main>
            </ProtectedRoute>
          } />
          <Route path="/activists" element={
            <ProtectedRoute>
              <Navigation />
              <main className="min-h-screen bg-gray-50">
                <Activists />
              </main>
            </ProtectedRoute>
          } />
          <Route path="/reports" element={
            <ProtectedRoute requireFullAccess>
              <Navigation />
              <main className="min-h-screen bg-gray-50">
                <Reports />
              </main>
            </ProtectedRoute>
          } />
          <Route path="/campaigns" element={
            <ProtectedRoute requireFullAccess>
              <Navigation />
              <main className="min-h-screen bg-gray-50">
                <Campaigns />
              </main>
            </ProtectedRoute>
          } />
          <Route path="/performance" element={
            <ProtectedRoute requireFullAccess>
              <Navigation />
              <main className="min-h-screen bg-gray-50">
                <Performance />
              </main>
            </ProtectedRoute>
          } />
          <Route path="/add-report" element={
            <ProtectedRoute requireFullAccess>
              <Navigation />
              <main className="min-h-screen bg-gray-50">
                <AddReport />
              </main>
            </ProtectedRoute>
          } />
          <Route path="/twitter-feed" element={
            <ProtectedRoute requireFullAccess>
              <Navigation />
              <main className="min-h-screen bg-gray-50">
                <TwitterFeed />
              </main>
            </ProtectedRoute>
          } />
          <Route path="/activist/:name" element={
            <ProtectedRoute requireFullAccess>
              <Navigation />
              <main className="min-h-screen bg-gray-50">
                <ActivistProfile />
              </main>
            </ProtectedRoute>
          } />
          <Route path="/report/:activist/:slug" element={
            <ProtectedRoute requireFullAccess>
              <Navigation />
              <main className="min-h-screen bg-gray-50">
                <ReportDetails />
              </main>
            </ProtectedRoute>
          } />
          <Route path="/scrapers" element={
            <ProtectedRoute requireFullAccess>
              <Navigation />
              <main className="min-h-screen bg-gray-50 px-4 sm:px-6 py-8 max-w-6xl mx-auto">
                <ScraperMonitoring />
              </main>
            </ProtectedRoute>
          } />
          <Route path="/backtesting-live" element={
            <ProtectedRoute requireFullAccess>
              <Navigation />
              <main className="min-h-screen bg-gray-50">
                <BacktestLive />
              </main>
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute requireAdmin>
              <Navigation />
              <main className="min-h-screen bg-gray-50">
                <UserManagement />
              </main>
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
    </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
